<template>
  <PageLayout class="users-send-invites">
    <template slot="header">
      <div class="d-flex m-3 mx-lg-0">
        <Breadcrumb :list="breadcrumbList" />
        <button
          v-if="!$can('access', 'read-only')"
          type="button"
          @click="$refs.userImporterModal.show()"
          class="btn btn-tertiary btn-lg mr-3"
          :title="labels.import"
          :disabled="isBasicPlan"
        >
          <i class="material-icons">
            file_upload
          </i>
          <span class="d-none d-lg-inline-block ml-2">
            {{ labels.import }}
          </span>
        </button>
      </div>
    </template>
    <template slot="main">
      <b-card :sub-title="labels.inviteMessage" id="sendInvitesCard">
        <b-row slot="header">
          <b-col lg="9">
            <h4 class="card-title">
              {{ labels.invite }}
            </h4>
          </b-col>
          <b-col lg="3" class="summary-content">
            <licenses-summary>
              <span slot="usersCount">
                {{ licenses.activatedUsersCount + invites.length }}
              </span>
            </licenses-summary>
          </b-col>
        </b-row>
        <b-form @submit.prevent="doSendInvites" ref="users-form">
          <b-list-group flush :key="index" v-for="(invite, index) in invites">
            <b-list-group-item>
              <b-row align-h="end" class="mr-4" v-if="invites.length > 1">
                <b-link
                  :id="`remove-users-form-${index}`"
                  class="link text-primary"
                  @click="removeFormInstance(index)"
                >
                  <i class="mdi mdi-minus-circle-outline "></i>
                  {{ labels.remove }}
                </b-link>
              </b-row>

              <p class="bold">{{ labels.basicInformation }}</p>

              <b-row class="mt-3">
                <b-col cols="12" xl="6">
                  <CustomInput
                    name="firstName"
                    :index="index"
                    :label="labels.input.firstName"
                    :title="labels.input.textWarning"
                    :labelWrong="labels.input.firstNameWrong"
                    :value="invites[index].firstName"
                    @update="
                      validateSendInvitesForm('firstName', $event, index)
                    "
                  >
                  </CustomInput>
                </b-col>

                <b-col cols="12" xl="6">
                  <CustomInput
                    name="lastName"
                    :index="index"
                    :label="labels.input.lastName"
                    :title="labels.input.textWarning"
                    :labelWrong="labels.input.lastNameWrong"
                    :value="invites[index].lastName"
                    @update="validateSendInvitesForm('lastName', $event, index)"
                  >
                  </CustomInput>
                </b-col>

                <b-col cols="12" xl="6">
                  <CustomInput
                    type="email"
                    :name="`email${index}`"
                    :index="index"
                    :title="labels.input.emailWarning"
                    :label="invite.emailLabel"
                    :isValid="invite.isValidEmail"
                    :labelWrong="labels.input.emailWrong"
                    :emailExists="invite.emailExists"
                    :emailExistsText="labels.input.emailExists"
                    :labelUp="invite.emailExists"
                    :value="invites[index].email"
                    @update="validateSendInvitesForm('email', $event, index)"
                  />
                </b-col>

                <b-col cols="12" xl="6">
                  <b-form-group>
                    <b-form-select
                      size="lg"
                      required
                      :id="'selectRole' + (index || '')"
                      :value="invites[index].role"
                      @input="validateSendInvitesForm('role', $event, index)"
                    >
                      <option value="admin">
                        {{ labels.input.roles.admin }}
                      </option>
                      <option value="manager">
                        {{ labels.input.roles.manager }}
                      </option>
                      <option value="user">
                        {{ labels.input.roles.user }}
                      </option>
                    </b-form-select>
                    <label class="custom-input-label">{{ labels.type }}</label>
                    <label class="mdi mdi-chevron-down"></label>
                  </b-form-group>
                </b-col>

                <b-col cols="12" xl="6" v-if="$can('super', 'user')">
                  <CustomInput
                    name="code"
                    :isRequired="false"
                    :label="resources.page.home.users.edit.code"
                    :labelWrong="resources.page.home.users.edit.code"
                    :value="invites[index].code"
                    @input="validateSendInvitesForm('code', $event, index)"
                  />
                </b-col>

                <b-col cols="12" xl="6">
                  <CustomInput
                    name="date"
                    :isRequired="false"
                    :hasMask="true"
                    :label="resources.page.home.users.edit.birthDate"
                    :labelWrong="resources.page.home.users.edit.birthDate"
                    :value="formatBirthDate(invites[index].birthDate)"
                    @update="updateBirthDate($event.value, index)"
                  />
                </b-col>
              </b-row>

              <p
                class="bold"
                v-if="userAttributesLoading || userAttributes.length > 0"
              >
                {{ labels.userAttributes }}
              </p>

              <b-row class="pt-3" v-if="userAttributesLoading">
                <b-col>
                  {{ labels.userAttributesLoading }}
                </b-col>
              </b-row>
              <b-row :class="{ 'pt-3': userAttributes.length }" v-else>
                <b-col
                  required
                  class="filter-select-item"
                  v-for="userAttribute in userAttributes"
                  :key="`user-attribute-${userAttribute.id}`"
                  cols="12"
                  xl="6"
                >
                  <label class="custom-input-label">
                    {{ userAttribute.label }}
                  </label>
                  <treeselect
                    ref="treeselect"
                    name="user-attributes-select"
                    :placeholder="labels.select"
                    :clearAllText="labels.cleanSelect"
                    :options="userAttribute.values"
                    :multiple="multiple"
                    :clearable="clearable"
                    :searchable="searchable"
                    :open-on-click="openOnClick"
                    :open-on-focus="openOnFocus"
                    :close-on-select="closeOnSelect"
                    :always-open="alwaysOpen"
                    @input="
                      updateUserAttribute($event, userAttribute.id, index)
                    "
                    :value="
                      invites[index].attributes
                        .filter(a => a.key === userAttribute.id)
                        .map(a => a.value)
                    "
                  >
                  </treeselect>
                </b-col>
              </b-row>
            </b-list-group-item>
          </b-list-group>

          <b-row align-h="end" class="mr-4 pb-2 pt-4">
            <b-link
              id="addUsersForm"
              class="link text-primary"
              @click="appendFormInstance"
            >
              <i class="mdi mdi-plus-circle-outline "></i>
              {{ labels.addUsers }}
            </b-link>
          </b-row>

          <b-list-group flush class="mt-3">
            <b-list-group-item>
              <p class="info-message">
                <span class="bold">{{ labels.sendNow }}:</span>
                {{ labels.sendNowMessage }}
              </p>
              <p class="info-message">
                <span class="bold">{{ labels.sendLater }}:</span>
                {{ labels.sendLaterMessage }}
              </p>
              <b-row align-h="end">
                <b-button
                  style="max-width:200px"
                  :disabled="submitInProgress"
                  variant="secondary"
                  class="col-12 mt-2 mr-2"
                  :title="labels.sendLaterMessage"
                  @click="doSendInvitesLater"
                >
                  <i
                    class="mdi mdi-spin pr-2"
                    v-bind:class="{ 'mdi-loading': submitInProgress }"
                  ></i>
                  {{ labels.sendLater }}
                </b-button>
                <b-button
                  ref="submit-button"
                  style="max-width:200px"
                  :disabled="submitInProgress"
                  type="submit"
                  variant="primary"
                  class="col-12 mt-2 mr-2"
                  :title="labels.sendNowMessage"
                >
                  <i
                    class="mdi mdi-spin pr-2"
                    v-bind:class="{ 'mdi-loading': submitInProgress }"
                  ></i>
                  {{ labels.sendNow }}
                </b-button>
              </b-row>
            </b-list-group-item>
          </b-list-group>
        </b-form>
      </b-card>

      <UserImporter ref="userImporterModal" />

      <modal-exceed-license ref="exceedLicense">
        <p>
          {{ labels.exceedLicense.messagePartOne }}
          {{ licenses.remainingLicenses }}
          {{ labels.exceedLicense.messagePartTwo }}
        </p>
      </modal-exceed-license>
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import CustomInput from '@/components/Shared/Form/CustomInput.vue'
import PageLayout from '@/components/Home/PageLayout.vue'
import ModalExceedLicense from '@/components/Modal/ModalExceedLicense.vue'
import LicensesSummary from '@/components/Shared/LicensesSummary.vue'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import { HIGH } from '@/helpers/messages-priority'
import { isEmail } from '@/helpers/validator'
import UserImporter from '@/components/Home/Users/UserImporter.vue'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import { isBasicPlan } from '@/service/user'

export default {
  mounted() {
    this.$root.$emit('setPageTitle', {
      context: this,
      path: 'users/sendInvites',
    })
  },
  components: {
    CustomInput,
    Breadcrumb,
    PageLayout,
    ModalExceedLicense,
    LicensesSummary,
    UserImporter,
    Treeselect,
  },

  data() {
    return {
      msg: undefined,
      submitInProgress: false,
      multiple: true,
      clearable: true,
      searchable: false,
      openOnClick: true,
      openOnFocus: true,
      closeOnSelect: true,
      alwaysOpen: false,
    }
  },

  computed: {
    ...mapGetters({
      resources: 'resources',
      licenses: 'company/licenses',
      sendInvites: 'sendInvites/sendInvites',
      userAttributes: 'userAttributes/filterUserAttributes',
      userAttributesLoading: 'userAttributes/filterLoading',
    }),
    invites() {
      return this.sendInvites.map(invite => {
        const emailLabel = this.getEmailLabel(
          invite.email,
          invite.emailEntered,
          invite.emailExists
        )
        const isValidEmail = this.getIsValidEmail(
          invite.email,
          invite.emailEntered,
          invite.emailExists
        )
        return { ...invite, emailLabel, isValidEmail }
      })
    },
    labels() {
      const input = this.resources.general.input
      const response500 = this.resources.general.errors.response500
      const invitedSuccessfully = this.resources.general.message
        .invitedSuccessfully

      return {
        ...this.resources.page.invite,
        input,
        response500,
        invitedSuccessfully,
      }
    },
    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.settings,
        },
        {
          label: this.labels.users,
          link: { name: 'dashboard.users' },
        },
        {
          label: this.labels.inviteUsers,
        },
      ]
    },
    isBasicPlan() {
      return isBasicPlan()
    },
  },
  created: function() {
    if (!this.isBasicPlan) {
      this.$store.dispatch('userAttributes/loadUserAttributesFilter')
    }
    this.$store.dispatch('company/loadLicenses')
    this.$store.commit('sendInvites/reset')
  },
  methods: {
    updateBirthDate(birthDate, index) {
      if (!birthDate || birthDate === '') {
        return this.$store.dispatch('sendInvites/validateSendInvitesForm', {
          property: 'birthDate',
          value: null,
          index,
        })
      }

      const i = birthDate.split('/')
      const date = i.length === 3 ? `${i[2]}-${i[1]}-${i[0]}` : birthDate
      return this.$store.dispatch('sendInvites/validateSendInvitesForm', {
        property: 'birthDate',
        value: date,
        index,
      })
    },
    formatBirthDate(date) {
      if (!date || date === '') {
        return null
      }
      const i = date.split('-')
      return i.length === 3 ? `${i[2]}/${i[1]}/${i[0]}` : date
    },
    sendInvitesError(error) {
      this.submitInProgress = false
      this.$root.$emit('app:message:show', {
        priority: HIGH,
        text: this.getMessage(error),
        status: 'danger',
      })
    },
    sendInvitesSuccess() {
      this.$root.$emit('track:app', {
        event: 'Convidou usuarios',
      })
      this.$root.$emit('app:message:show', {
        priority: HIGH,
        text: this.labels.invitedSuccessfully,
        status: 'success',
      })
      this.submitInProgress = false
      this.$router.push('/users')
      this.$store.commit('sendInvites/reset')
    },
    getMessage(error) {
      let message
      try {
        message =
          error.response.data.message +
          ': ' +
          error.response.data.extra.join(', ')
      } catch (e) {
        message = error.response.data.message
          ? error.response.data.message
          : this.labels.response500
      }
      return message
    },
    getEmailLabel(
      email = '',
      emailEntered = undefined,
      emailExists = undefined
    ) {
      email = email.trim()

      if (!isNaN(email)) return this.labels.input.emailTitle // tratativa mailFake
      if (emailEntered) return this.labels.input.emailEntered
      if (emailExists) return this.labels.input.emailExists
      if (email.length && !isEmail(email)) return this.labels.input.emailWrong
      return this.labels.input.emailTitle
    },
    getIsValidEmail(
      value = '',
      emailEntered = undefined,
      emailExists = undefined
    ) {
      value = value.trim()
      if (!isNaN(value)) return undefined // tratativa mailFake
      if (emailEntered || emailExists) return false
      if (value.length) return isEmail(value)

      return undefined
    },
    appendFormInstance() {
      const { remainingLicenses } = this.licenses
      if (remainingLicenses > this.invites.length) {
        this.$store.commit('sendInvites/appendFormInstance')
      } else {
        this.$refs.exceedLicense.show()
      }
    },
    removeFormInstance(index) {
      this.$store.commit('sendInvites/removeFormInstance', { index })
    },
    validateSendInvitesForm(property, input, index) {
      const { value, event } =
        typeof input === 'object' ? input : { value: input, undefined }
      let prop = { property, value, index, event }
      this.$store.dispatch('sendInvites/validateSendInvitesForm', prop)
    },
    doSendInvites() {
      if (this.submitInProgress) return

      this.submitInProgress = true
      this.$store
        .dispatch('sendInvites/doSendInvites', {
          sendNow: true,
          $can: this.$can,
        })
        .then(this.sendInvitesSuccess)
        .catch(this.sendInvitesError)
    },
    doSendInvitesLater() {
      if (this.submitInProgress) return

      if (!this.$refs['users-form'].checkValidity()) {
        return this.$refs['submit-button'].click()
      }

      this.submitInProgress = true
      this.$store
        .dispatch('sendInvites/doSendInvites', {
          sendNow: false,
          $can: this.$can,
        })
        .then(this.sendInvitesSuccess)
        .catch(this.sendInvitesError)
    },
    updateUserAttribute(values, userAttributeId, index) {
      this.$store.dispatch('sendInvites/updateUserAttribute', {
        values,
        userAttributeId,
        index,
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.card {
  .card-header {
    border-bottom: none !important;
    padding-top: 20px;
    padding-right: 0;
    padding-bottom: 0;
    margin-bottom: 25px;

    @media (max-width: 991.98px) {
      // lg
      margin-top: 0;
      margin-bottom: 0;
      font-size: medium;
      font-weight: normal;
    }

    h4 {
      font-size: 24px;
      font-weight: 500;
      margin: 15px 0 0;
    }

    > .row {
      position: relative;
      width: 100%;
    }

    .summary-content {
      padding-right: 0;
      position: absolute;
      right: -16px;
      z-index: 10;

      .summary {
        margin-right: 0;
      }
    }
  }

  .card-body {
    padding-top: 0;

    /deep/ .is-code:not(:focus):not(.is-valid),
    /deep/ .is-date:not(:focus):not(.is-valid) {
      background-color: map-get($theme-colors, 'white') !important;
      border: 1px solid #ced4da !important;
    }

    /deep/ .custom-input-label {
      font-size: 12px;
      color: map-get($theme-colors, 'gray03') !important;
      top: -18px;
    }

    .select-icon {
      margin-bottom: 0 !important;
      top: 0;
    }

    .info-message {
      font-size: 0.8rem;
    }

    .filter-select-item {
      margin-bottom: 35px;

      .custom-input-label {
        left: 40px !important;
      }

      /deep/ .vue-treeselect {
        .vue-treeselect__menu {
          border-radius: 30px !important;
          overflow-x: auto;
        }

        .vue-treeselect__checkbox--checked {
          border-color: map-get($theme-colors, 'primary') !important;
          background: map-get($theme-colors, 'primary') !important;
        }

        .vue-treeselect__control {
          border-radius: 30px !important;
          height: 48px;
        }

        .vue-treeselect__placeholder {
          margin: 7px;
          font-size: 10pt;
        }

        .vue-treeselect__multi-value-item {
          border-radius: 30px !important;
          background-color: map-get($theme-colors, 'gray08');
          color: map-get($theme-colors, 'primary') !important;

          .vue-treeselect__value-remove {
            color: map-get($theme-colors, 'primary') !important;
          }

          .vue-treeselect__multi-value-label {
            white-space: normal;
          }
        }

        .vue-treeselect__label-container {
          &:hover {
            .vue-treeselect__checkbox--unchecked {
              border-color: map-get($theme-colors, 'primary');
            }
          }

          .vue-treeselect__label {
            overflow: inherit;
          }
        }

        .vue-treeselect__option--selected {
          background-color: map-get($theme-colors, 'gray08');
        }
      }
    }
  }
}
</style>
